import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Switch from 'flarum/common/components/Switch';
import extractText from 'flarum/common/utils/extractText';
import app from 'flarum/forum/app';
import CreateTicketModal from './CreateTicketModal';
import TicketList from './TicketList';
const T = (key, params) => app.translator.trans('cengizkemec-support.forum.' + key, params);
const OPEN_STATUSES = ['new', 'answered', 'pending'];
export default class SupportPage extends Page {
  loading = true;
  filter = 'all';
  error = null;
  savingPrefs = false;
  oninit(vnode) {
    super.oninit(vnode);

    // The browser tab must say "Support", not the previous page's title.
    app.setTitle(extractText(T('meta_title_text')));

    // Guests are welcome to land here, but they cannot read tickets: show a
    // call to action that invites them to sign in or register instead.
    if (!app.session.user) {
      this.loading = false;
      return;
    }
    app.store.find('tickets', {
      sort: '-createdAt',
      include: 'category.department,assignee,user,replies'
    }).then(() => {
      this.loading = false;
      m.redraw();
    }).catch(e => {
      this.loading = false;
      this.error = e?.response?.errors?.[0]?.detail || e?.message || T('error_load');
      m.redraw();
    });
  }
  view() {
    const isGuest = !app.session.user;
    return m(PageStructure, {
      className: "SupportPage",
      hero: () => this.hero(isGuest)
    }, isGuest ? this.guest() : this.loading ? m("div", {
      className: "SupportLoading"
    }, m(LoadingIndicator, null)) : this.content());
  }
  hero(isGuest) {
    return m("div", {
      className: "container"
    }, m("div", {
      className: "SupportHero"
    }, m("div", {
      className: "SupportHero-main"
    }, m("h2", {
      className: "SupportHero-title"
    }, m("i", {
      className: "icon fas fa-headset"
    }), " ", T('title')), m("p", {
      className: "SupportHero-subtitle"
    }, T('subtitle'))), !isGuest && m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => app.modal.show(CreateTicketModal)
    }, T('new_ticket'))));
  }
  guest() {
    return m("div", {
      className: "SupportGuest"
    }, m("div", {
      className: "SupportGuest-icon"
    }, m("i", {
      className: "icon fas fa-lock"
    })), m("h3", {
      className: "SupportGuest-title"
    }, T('guest_title')), m("p", {
      className: "SupportGuest-text"
    }, T('guest_text')), m("div", {
      className: "SupportGuest-actions"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-right-to-bracket",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
    }, T('guest_login')), m(Button, {
      className: "Button",
      icon: "fas fa-user-plus",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'))
    }, T('guest_register'))));
  }

  /**
   * A single switch for all ticket notifications (alert, email and push).
   *
   * The individual per-method checkboxes still live on the forum settings
   * page; this is the quick "stop emailing me about tickets" control right
   * where support tickets are managed. Preference keys are discovered from
   * the user's stored preferences so it keeps working if Flarum or FoF PWA
   * add another notification method.
   */
  notificationToggle() {
    const user = app.session.user;
    if (!user) return null;
    const preferences = user.preferences() || {};
    const keys = Object.keys(preferences).filter(key => /^notify_(newTicket|ticketReply)_/.test(key));

    // No keys means no notification type is registered for the actor's
    // environment; showing a dead switch would only confuse people.
    if (!keys.length) return null;
    const enabled = keys.some(key => !!preferences[key]);
    return m("div", {
      className: 'SupportNotifToggle' + (this.savingPrefs ? ' is-loading' : '')
    }, m(Switch, {
      state: enabled,
      loading: this.savingPrefs,
      disabled: this.savingPrefs,
      onchange: () => this.toggleNotifications(keys, !enabled)
    }, T('notifications.label')), m("div", {
      className: "SupportNotifToggle-help"
    }, enabled ? T('notifications.on') : T('notifications.off')), m("div", {
      className: "SupportNotifToggle-note"
    }, T('notifications.help')));
  }
  async toggleNotifications(keys, enabled) {
    const user = app.session.user;
    if (!user || this.savingPrefs) return;
    const preferences = user.preferences() || {};
    keys.forEach(key => preferences[key] = enabled);
    this.savingPrefs = true;
    m.redraw();
    try {
      await user.save({
        preferences
      });
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, e?.response?.errors?.[0]?.detail || e?.message || T('error_preferences'));
    } finally {
      this.savingPrefs = false;
      m.redraw();
    }
  }
  content() {
    const isStaff = !!app.forum.attribute('canSupportViewAll') || !!app.forum.attribute('canSupportManage');
    const tickets = app.store.all('tickets');
    const total = tickets.length;
    const open = tickets.filter(t => OPEN_STATUSES.indexOf(t.status()) >= 0).length;
    const closed = total - open;
    const tabs = [['all', T('tab_all'), total], ['open', T('tab_open'), open], ['closed', T('tab_closed'), closed]];
    return m("div", null, this.notificationToggle(), m("div", {
      className: "SupportStats"
    }, m("div", {
      className: "SupportStat"
    }, m("div", {
      className: "SupportStat-value"
    }, total), m("div", {
      className: "SupportStat-label"
    }, T('stat_total'))), m("div", {
      className: "SupportStat"
    }, m("div", {
      className: "SupportStat-value"
    }, open), m("div", {
      className: "SupportStat-label"
    }, T('stat_open'))), m("div", {
      className: "SupportStat"
    }, m("div", {
      className: "SupportStat-value"
    }, closed), m("div", {
      className: "SupportStat-label"
    }, T('stat_closed')))), m("div", {
      className: "SupportTabs"
    }, tabs.map(([key, label, count]) => m("button", {
      key: key,
      type: "button",
      className: 'SupportTab' + (this.filter === key ? ' active' : ''),
      onclick: () => {
        this.filter = key;
        m.redraw();
      }
    }, label, " ", m("span", {
      className: "SupportTab-count"
    }, count)))), this.error ? m("div", {
      className: "Alert Alert--error"
    }, this.error) : m(TicketList, {
      filter: this.filter,
      isStaff: isStaff
    }));
  }
}
flarum.reg.add('cengizkemec-support-tickets', 'forum/components/SupportPage', SupportPage);